import axios from "axios";
import {
  ArcgisEndpointConstant,
  ITemperatureRepository,
  ResponseValueObject,
  TemperatureArea,
  TemperatureLevel,
  TemperatureQuery,
  TemperatureUnitEntity,
} from "@gishub/domain";

/** Campos del servicio de límites censales (única fuente: `LIMITE_FIELDS`). */
const F_DEP = ArcgisEndpointConstant.LIMITE_FIELDS.DEPARTAMENTO;
const F_PROV = ArcgisEndpointConstant.LIMITE_FIELDS.PROVINCIA;

/** API de pronóstico de Open-Meteo (abierta, sin clave). */
const OPEN_METEO_FORECAST = "https://api.open-meteo.com/v1/forecast";

/** Configuración de la subcapa de límites por nivel territorial. */
interface LimiteLayerConfig {
  layerId: number;
  outFields: string;
  nameField: string;
  codeField: string;
}

/** Unidad territorial cruda (antes de unirle la temperatura). */
interface RawUnit {
  code: string;
  name: string;
  longitude: number;
  latitude: number;
  geometry: unknown;
}

/** Temperatura resuelta para un punto. */
interface RawTemperature {
  temperature: number | null;
  min: number | null;
  max: number | null;
  time: string | null;
}

/**
 * Repositorio de temperatura por unidad territorial.
 *
 * Cruza dos fuentes públicas y complementarias:
 *  1. **SERV_LIMITE_CENSAL (MIDAGRI)** → límites de departamentos (subcapa 0) y
 *     provincias (subcapa 1). Se consulta con `returnGeometry=true` y
 *     `outSR=4326`; el punto representativo (centroide de área) se calcula aquí,
 *     porque este servicio NO devuelve `returnCentroid` (verificado: llega vacío).
 *  2. **Open-Meteo** → temperatura actual a 2 m + mínima/máxima del día en los
 *     centroides. Admite N coordenadas separadas por coma en UNA sola petición.
 *
 * Cuando se reciben `areas` (polígonos de alerta SENAMHI), cada unidad se
 * filtra con una intersección espacial real contra el servicio de límites, de
 * modo que sólo se devuelven unidades afectadas.
 */
export class OpenMeteoTemperatureRepository implements ITemperatureRepository {
  private readonly layers: Record<TemperatureLevel, LimiteLayerConfig> = {
    department: {
      layerId: ArcgisEndpointConstant.LIMITE_DEPARTAMENTO.sublayer,
      outFields: `${F_DEP.code},${F_DEP.name}`,
      nameField: F_DEP.name,
      codeField: F_DEP.code,
    },
    province: {
      layerId: ArcgisEndpointConstant.LIMITE_PROVINCIAL.sublayer,
      outFields: `${F_PROV.code},${F_PROV.name}`,
      nameField: F_PROV.name,
      codeField: F_PROV.code,
    },
  };

  async getUnits(
    query: TemperatureQuery,
  ): Promise<ResponseValueObject<TemperatureUnitEntity[]>> {
    try {
      const config = this.layers[query.level];
      const raw = query.areas?.length
        ? await this.queryIntersecting(config, query.areas)
        : await this.queryAll(config);
      const temperatures = await this.fetchTemperatures(raw);
      const units = raw.map(
        (unit, index) =>
          new TemperatureUnitEntity(
            unit.code,
            unit.name,
            query.level,
            unit.longitude,
            unit.latitude,
            temperatures[index]?.temperature ?? null,
            temperatures[index]?.min ?? null,
            temperatures[index]?.max ?? null,
            temperatures[index]?.time ?? null,
            unit.geometry,
          ),
      );
      return new ResponseValueObject(true, units);
    } catch (error) {
      return new ResponseValueObject<TemperatureUnitEntity[]>(
        false,
        [],
        (error as Error)?.message ?? "No se pudo obtener la temperatura",
      );
    }
  }

  // ── Cruce espacial con los límites administrativos ────────────────

  /** Unidades del nivel completo (sin filtro espacial). */
  private queryAll(config: LimiteLayerConfig): Promise<RawUnit[]> {
    return this.limiteQuery(config, {});
  }

  /**
   * Unidades que intersecan alguno de los polígonos de alerta. Se consulta
   * una vez por polígono —en paralelo— y se deduplica por código (una unidad
   * puede tocar varios avisos). El navegador limita las conexiones por host,
   * así que no se satura el servicio.
   */
  private async queryIntersecting(
    config: LimiteLayerConfig,
    areas: TemperatureArea[],
  ): Promise<RawUnit[]> {
    const batches = await Promise.all(
      areas.map((area) =>
        this.limiteQuery(config, {
          geometry: JSON.stringify({
            rings: area,
            spatialReference: { wkid: 4326 },
          }),
          geometryType: "esriGeometryPolygon",
          spatialRel: "esriSpatialRelIntersects",
          inSR: "4326",
        }),
      ),
    );
    const byCode = new Map<string, RawUnit>();
    for (const units of batches) {
      for (const unit of units) byCode.set(unit.code, unit);
    }
    return [...byCode.values()];
  }

  /** Ejecuta una consulta `query` contra una subcapa de límites (POST, admite geometrías grandes). */
  private async limiteQuery(
    config: LimiteLayerConfig,
    extra: Record<string, string>,
  ): Promise<RawUnit[]> {
    const params = new URLSearchParams({
      where: "1=1",
      outFields: config.outFields,
      returnGeometry: "true",
      outSR: "4326",
      f: "json",
      ...extra,
    });
    const response = await axios.post(
      `${ArcgisEndpointConstant.LIMITE_CENSAL_MAPSERVER}/${config.layerId}/query`,
      params,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        timeout: 25000,
      },
    );
    return this.parseFeatures(config, response.data);
  }

  private parseFeatures(config: LimiteLayerConfig, data: any): RawUnit[] {
    const units: RawUnit[] = [];
    for (const feature of data?.features ?? []) {
      const attributes = feature?.attributes ?? {};
      const code = String(attributes[config.codeField] ?? "");
      const name = attributes[config.nameField];
      const rings = feature?.geometry?.rings;
      if (!code || name == null || !Array.isArray(rings) || rings.length === 0) {
        continue;
      }
      const centroid = this.centroidOf(rings);
      if (!centroid) continue;
      units.push({
        code,
        name: String(name),
        longitude: centroid.longitude,
        latitude: centroid.latitude,
        geometry: feature.geometry,
      });
    }
    return units;
  }

  /**
   * Centroide de área de un polígono esriJSON (rings WGS84). Usa la fórmula
   * clásica de polígono; los huecos (anillos con orientación inversa) restan
   * de forma natural. Si el área es ~0 (anillo degenerado) cae al promedio de
   * vértices, que siempre devuelve un punto utilizable para muestrear.
   */
  private centroidOf(
    rings: number[][][],
  ): { longitude: number; latitude: number } | null {
    const at = (ring: number[], index: number): number => {
      const value = ring[index];
      return typeof value === "number" ? value : 0;
    };
    let area2 = 0;
    let cx = 0;
    let cy = 0;
    let meanX = 0;
    let meanY = 0;
    let vertices = 0;
    for (const ring of rings) {
      const size = ring?.length ?? 0;
      for (let i = 0; i < size; i++) {
        const j = (i + 1) % size;
        const current = ring[i];
        const next = ring[j];
        const x0 = at(current, 0);
        const y0 = at(current, 1);
        const x1 = at(next, 0);
        const y1 = at(next, 1);
        const cross = x0 * y1 - x1 * y0;
        area2 += cross;
        cx += (x0 + x1) * cross;
        cy += (y0 + y1) * cross;
        meanX += x0;
        meanY += y0;
        vertices++;
      }
    }
    if (vertices === 0) return null;
    if (area2 !== 0) {
      const longitude = cx / (3 * area2);
      const latitude = cy / (3 * area2);
      if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
        return { longitude, latitude };
      }
    }
    return { longitude: meanX / vertices, latitude: meanY / vertices };
  }

  // ── Temperatura (Open-Meteo) ──────────────────────────────────────

  /**
   * Temperatura actual + mínima/máxima del día para todos los centroides en
   * UNA sola petición (coordenadas separadas por coma). Open-Meteo devuelve
   * un objeto cuando hay una sola coordenada y una lista cuando hay varias.
   */
  private async fetchTemperatures(units: RawUnit[]): Promise<RawTemperature[]> {
    if (units.length === 0) return [];
    const response = await axios.get(OPEN_METEO_FORECAST, {
      params: {
        latitude: units.map((unit) => unit.latitude).join(","),
        longitude: units.map((unit) => unit.longitude).join(","),
        current: "temperature_2m",
        daily: "temperature_2m_max,temperature_2m_min",
        timezone: "auto",
        forecast_days: 1,
      },
      timeout: 15000,
    });
    const list: any[] = Array.isArray(response.data)
      ? response.data
      : [response.data];
    return units.map((_, index) => {
      const point = list[index];
      return {
        temperature: this.num(point?.current?.temperature_2m),
        min: this.num(point?.daily?.temperature_2m_min?.[0]),
        max: this.num(point?.daily?.temperature_2m_max?.[0]),
        time: point?.current?.time ?? null,
      };
    });
  }

  private num(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  }
}
